import React, { useState, useEffect, useRef } from 'react';
import { Dialog } from 'primereact/dialog';
import { InputText } from 'primereact/inputtext';
import { Button } from 'primereact/button';
import { Message } from 'primereact/message';
import { classNames } from 'primereact/utils';
import axiosInstance from '../../config/axios.config';

const OTPVerificationModal = ({ 
    visible, 
    onHide, 
    email, 
    onVerified,
    onResend 
}) => {
    const [otp, setOtp] = useState(['', '', '', '', '', '']);
    const [loading, setLoading] = useState(false);
    const [resending, setResending] = useState(false);
    const [error, setError] = useState('');
    const [timer, setTimer] = useState(120); // 2 minutes
    const [canResend, setCanResend] = useState(false);
    
    const inputRefs = useRef([]);

    // Timer countdown
    useEffect(() => {
        let interval;
        if (visible && timer > 0) {
            interval = setInterval(() => {
                setTimer((prev) => {
                    if (prev <= 1) {
                        setCanResend(true);
                        return 0;
                    }
                    return prev - 1;
                });
            }, 1000);
        }
        return () => clearInterval(interval);
    }, [visible, timer]);

    // Reset when modal opens
    useEffect(() => {
        if (visible) {
            setOtp(['', '', '', '', '', '']);
            setError('');
            setTimer(120);
            setCanResend(false);
            // Focus first input
            setTimeout(() => {
                inputRefs.current[0]?.focus();
            }, 100);
        }
    }, [visible]);

    const handleOtpChange = (value, index) => {
        // Only allow digits
        if (value && !/^\d$/.test(value)) return;

        const newOtp = [...otp];
        newOtp[index] = value;
        setOtp(newOtp);
        setError('');

        // Auto-focus next input
        if (value && index < 5) {
            inputRefs.current[index + 1]?.focus();
        }

        // Auto-submit when all digits entered
        if (value && index === 5 && newOtp.every(digit => digit)) {
            handleVerify(newOtp.join(''));
        }
    };

    const handleKeyDown = (e, index) => {
        // Handle backspace
        if (e.key === 'Backspace' && !otp[index] && index > 0) {
            inputRefs.current[index - 1]?.focus();
        }
        
        // Handle paste
        if (e.key === 'v' && (e.ctrlKey || e.metaKey)) {
            e.preventDefault();
            handlePaste(e);
        }
    };

    const handlePaste = async (e) => {
        const pastedData = e.clipboardData?.getData('text') || await navigator.clipboard.readText();
        const digits = pastedData.replace(/\D/g, '').slice(0, 6);
        
        if (digits.length === 6) {
            const newOtp = digits.split('');
            setOtp(newOtp);
            inputRefs.current[5]?.focus();
            handleVerify(digits);
        }
    };

    const handleVerify = async (otpString = null) => {
        const otpCode = otpString || otp.join('');
        
        if (otpCode.length !== 6) {
            setError('Please enter all 6 digits');
            return;
        }

        setLoading(true);
        setError('');

        try {
            const response = await axiosInstance.post('/auth/verify-otp', {
                email,
                otp: otpCode
            });

            if (response.data.success) {
                onVerified(otpCode);
                onHide();
            }
        } catch (error) {
            setError(error.response?.data?.message || 'Invalid OTP. Please try again.');
            // Clear OTP on error
            setOtp(['', '', '', '', '', '']);
            inputRefs.current[0]?.focus();
        } finally {
            setLoading(false);
        }
    };

    const handleResend = async () => {
        setResending(true);
        setError('');

        try {
            const response = await axiosInstance.post('/auth/resend-otp', {
                email
            });

            if (response.data.success) {
                // Reset timer
                setTimer(120);
                setCanResend(false);
                setOtp(['', '', '', '', '', '']);
                inputRefs.current[0]?.focus();
                
                if (onResend) {
                    onResend();
                }
            }
        } catch (error) {
            setError(error.response?.data?.message || 'Failed to resend OTP');
        } finally {
            setResending(false);
        }
    };

    const formatTime = (seconds) => {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins}:${secs.toString().padStart(2, '0')}`;
    };

    const footer = (
        <div className="flex flex-column gap-3">
            <div className="flex gap-2">
                <Button 
                    label="Verify"
                    onClick={() => handleVerify()}
                    disabled={otp.some(digit => !digit) || loading}
                    loading={loading}
                    className="flex-1"
                />
                <Button 
                    label={canResend ? "Resend OTP" : `Resend in ${formatTime(timer)}`}
                    onClick={handleResend}
                    disabled={!canResend || resending}
                    loading={resending}
                    className="p-button-text flex-1"
                />
            </div>
        </div>
    );

    return (
        <Dialog 
            visible={visible} 
            onHide={onHide}
            header="Email Verification"
            footer={footer}
            style={{ width: '450px' }}
            modal
            closable={!loading}
            draggable={false}
        >
            <div className="flex flex-column gap-4">
                <div className="text-center">
                    <p className="m-0 text-600">
                        We've sent a verification code to
                    </p>
                    <p className="mt-1 mb-0 font-semibold text-900">
                        {email}
                    </p>
                </div>

                {error && (
                    <Message 
                        severity="error" 
                        text={error}
                        className="w-full"
                    />
                )}

                <div className="flex flex-column gap-2">
                    <label className="text-600 text-sm">Enter 6-digit code</label>
                    <div className="flex gap-2 justify-content-center">
                        {otp.map((digit, index) => (
                            <InputText
                                key={index}
                                ref={el => inputRefs.current[index] = el}
                                value={digit}
                                onChange={(e) => handleOtpChange(e.target.value, index)}
                                onKeyDown={(e) => handleKeyDown(e, index)}
                                onPaste={index === 0 ? handlePaste : undefined}
                                maxLength={1}
                                className={classNames(
                                    'text-center text-xl font-bold',
                                    'w-3rem h-3rem',
                                    { 'p-invalid': error }
                                )}
                                disabled={loading}
                                autoComplete="off"
                            />
                        ))}
                    </div>
                </div>

                {!canResend && timer > 0 && (
                    <div className="text-center text-600 text-sm">
                        Code expires in {formatTime(timer)}
                    </div>
                )}

                <div className="text-center text-600 text-sm">
                    Didn't receive the code? Check your spam folder.
                </div>
            </div>
        </Dialog>
    );
};

export default OTPVerificationModal;